import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

// Context
import DialogContext from '@/context/DialogContext/DialogContext';

// Custom
import InsertDocumentDialog from '@/views/core/Documents/InsertDocumentDialog';
import useApi from '@/lib/api/useApi';

import { DCFile, DCRecord } from '@/@types/lib/dataController';
import { ClosingDetails } from '@/@types/components/authFormController';

import DataController from '@/lib/DataController';

import modelDocuments from '@/models/documents';
import AttachedDocumentDialog from '@/views/core/Documents/AttachedDocumentDialog';
import DocumentViews from '@/lib/documentViews';
import SnackbarContext, { SnackbarContextType } from '@/context/SnackbarContext/SnackbarContext';
import { IPickerModel } from '@/@types/models/model';

type HandleCloseFn = (result: ClosingDetails) => void;

export interface IDocuments {
  checkIfHasGid: (records: DCRecord[]) => boolean;
  showDocumentsDialog: (gid: string, onClose?: HandleCloseFn) => void;
  downloadDocument: (gid: string, subModel?: IPickerModel, isHistory?: boolean) => void;
  saveDocument: (file: DCFile) => void;
}
interface UseDocumentsProps {
  view?: DocumentViews;
}

// Hook
function useDocuments(props?: UseDocumentsProps): IDocuments {
  // Manage is default for backwards compatibility
  const view = props?.view ? props?.view : DocumentViews.Manage;

  const dc = new DataController(modelDocuments);

  const { hideDialog } = useContext(DialogContext);

  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const { t } = useTranslation();

  // const [isLoading, setIsLoading] = useState<boolean>(false);

  const refreshDocuments = async (gid: string): Promise<DCRecord[]> => {
    // setIsLoading(true);
    try {
      const url = view === DocumentViews.View ? `core/documents-for-entity-public/${gid}` : `${dc.apiPath}/${gid}`;
      const resp = await dc.GetData(url);
      if (resp.success) {
        if (resp.data && !Array.isArray(resp.data) && resp.data.files) {
          return resp.data.files as DCRecord[];
        }
        return [];
      }
      // eslint-disable-next-line no-console
      console.error('Error while fetching comments for gid:', gid, 'response:', resp);
      return [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error while fetching comments:', error);
      return [];
    } finally {
      // setIsLoading(false);
    }
  };

  const { showDialog } = useContext(DialogContext);

  const showDocumentsDialog = async (gid: string, onClose?: HandleCloseFn) => {
    refreshDocuments(gid).then((documents) => {
      if (view === DocumentViews.View) {
        if (documents && documents.length) {
          showPublicDocumentsDialog(gid, documents, onClose);
        } else {
          snackbarContext.showNotification(t('messages.no_documents'), 'info');
        }
      } else if (view === DocumentViews.Manage) {
        if (documents && documents.length) {
          showAttachedDocumentsDialog(gid, documents, onClose);
        } else {
          showNewDocumentDialog(gid, onClose);
        }
      }
    });
  };

  const showNewDocumentDialog = (gid: string, onClose?: HandleCloseFn) => {
    showDialog(
      InsertDocumentDialog,
      {
        dc,
        mode: 'insert',
        form: 'insert',
        onClose,
        initialRecord: {
          entity_gid: gid,
        },
        level: 2,
      },
      2
    );
  };

  const onAddNewDocument = async (result: ClosingDetails) => {
    if (!result) return;
    if (typeof result.success === 'boolean' && !result.success) return;

    if (result.dataChanged) {
      hideDialog(2);
    }
  };

  const showAttachedDocumentsDialog = (gid: string, documents: DCRecord[], onClose?: HandleCloseFn) => {
    showDialog(
      AttachedDocumentDialog,
      {
        dc,
        onClose,
        initialRecord: documents,
        gid,
        showNewDocumentDialog,
        onAddNewDocument,
      },
      1
    );
  };

  const showPublicDocumentsDialog = (gid: string, documents: DCRecord[], onClose?: HandleCloseFn) => {
    showDialog(
      AttachedDocumentDialog,
      {
        dc,
        onClose,
        initialRecord: documents,
        gid,
        showNewDocumentDialog,
        onAddNewDocument,
        view,
      },
      1
    );
  };

  // ###

  const checkIfHasGid = (records: DCRecord[]) => !!(records && records.length > 0 && Object.hasOwn(records[0], 'gid') === true);

  const downloadDocument = (gid: string, subModel?: IPickerModel, isDeleted?: boolean) => {
    // Fetch from db
    const apiInstance = useApi();
    const url = isDeleted && subModel?.alternateApiPath ? subModel?.alternateApiPath : subModel?.apiPath || 'core/documents';
    apiInstance.downloadWithMetadata(url, gid).then((resp) => {
      if (resp.success && resp.data) {
        const file = resp.data;

        if (!file.content) {
          // eslint-disable-next-line no-console
          console.error('File has no content');
        } else {
          saveDocument(file);
        }
      }
    });
  };

  const saveDocument = (file: DCFile) => {
    // Download to pc
    if (file.content) {
      const byteCharacters = atob(file.content);
      const byteNumbers = new Array(byteCharacters.length).fill(undefined).map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file.type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.click();
    }
  };

  return {
    checkIfHasGid,
    showDocumentsDialog,
    downloadDocument,
    saveDocument,
  };
}

export default useDocuments;
